import {
    FormControl,
    FormControlLabel,
    FormHelperText,
    Checkbox as MuiCheckbox,
} from '@mui/material';
import React, { useRef } from 'react';

import Locale from '../Locale';
import PropTypes from 'prop-types';
import startCase from 'lodash/startCase';
import useBoundCallback from '../../helpers/Hooks/useBoundCallback';
import { v4 as uuid } from 'uuid';
import { withFormikField } from './FormikFields';

/**
 * @typedef {Object} Base
 * @property {string|ReactNode} [label] The label content. Can be a string or a ReactNode.
 * @property {import('@mui/material').FormControlLabelProps} [FormLabelProps]
 * @property {Object=} translationOptions The `options` object provided to all internal <Locale/> translations for the TextField
 * @property {boolean} inverted Reverses the visual representation of the `checked` value
 *
 * @typedef {Base & Pick<import('@mui/material').FormControlProps, 'error' | 'fullWidth' | 'required'> & Pick<import('@mui/material').FormControlLabelProps, 'labelPlacement'> & Pick<import('@mui/material').TextFieldProps, 'helperText' | 'label'> & import('@mui/material').CheckboxProps} CheckboxProps
 */

/**
 * @type {React.ForwardRefExoticComponent<CheckboxProps>}
 */
export const Checkbox = React.forwardRef((props, ref) => {
    const {
        'aria-describedby': ariaDescribedBy,
        checked,
        disabled,
        error,
        FormHelperTextProps,
        FormLabelProps,
        fullWidth,
        helperText,
        id,
        inverted,
        labelPlacement,
        name,
        onChange: providedOnChange,
        required,
        translationOptions,
        value,
        label = name ? startCase(`${name}`) : undefined,
        // The following values may be passed by FormikField and we need to filter them out
        type,
        multiple,
        placeholder,
        ...remain
    } = props;

    const errorId = useRef(uuid());

    const onChange = useBoundCallback(
        (change, isInverted, event, value) => {
            if (change) {
                if (inverted) {
                    event.target.checked = !value;
                }
                change(event, isInverted ? !value : value);
            }
        },
        [providedOnChange, inverted]
    );

    return (
        <FormControl
            fullWidth={!!fullWidth}
            error={error}
            required={!!required}
            style={{ minWidth: 0 }}
        >
            <FormControlLabel
                key={value}
                label={
                    !label || React.isValidElement(label) ? (
                        label
                    ) : (
                        <Locale path={label} options={translationOptions} />
                    )
                }
                labelPlacement={labelPlacement}
                {...FormLabelProps}
                control={
                    <MuiCheckbox
                        inputProps={{ id }}
                        inputRef={ref}
                        checked={
                            checked !== undefined ? (inverted ? !checked : checked) : undefined
                        }
                        color="secondary"
                        disabled={disabled}
                        name={name}
                        value={String(value)}
                        type="checkbox"
                        aria-describedby={
                            !helperText // If there is an error, the aria-describedby needs to reference the error message, and that's handled by MUI internally
                                ? ariaDescribedBy
                                : errorId.current
                        }
                        onChange={onChange}
                        {...remain}
                    />
                }
            />
            <FormHelperText
                {...FormHelperTextProps}
                id={errorId.current}
                sx={{ whiteSpace: 'pre-line' }}
            >
                {helperText}
            </FormHelperText>
        </FormControl>
    );
});

Checkbox.propTypes = {
    'aria-describedby': PropTypes.string,
    checked: PropTypes.bool,
    disabled: PropTypes.bool,
    error: PropTypes.bool,
    FormHelperTextProps: PropTypes.object,
    FormLabelProps: PropTypes.object,
    fullWidth: PropTypes.bool,
    helperText: PropTypes.any,
    id: PropTypes.string,
    indeterminate: PropTypes.bool,
    inverted: PropTypes.bool,
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    labelPlacement: PropTypes.oneOf(['end', 'start', 'top', 'bottom']),
    multiple: PropTypes.bool,
    name: PropTypes.string,
    onChange: PropTypes.func,
    placeholder: PropTypes.string,
    required: PropTypes.bool,
    translationOptions: PropTypes.object,
    type: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
};

export default Checkbox;

export const FormikCheckbox = withFormikField(Checkbox, { type: 'checkbox' });
FormikCheckbox.displayName = 'FormikCheckbox';
